/**
 * COP Dwells Service
 * Fetches device dwell data for COP dashboard
 */

export interface DeviceDwell {
  id: number;
  device_reference_id: number;
  entity_id: string;
  device_id: string;
  device_type: string;
  course_id?: string;
  latitude: number;
  longitude: number;
  duration: string;
  duration_seconds: number;
  start_time: string;
  end_time: string;
  place_name?: string;
  place_address?: string;
  place_type?: string;
}

export interface DwellsResponse {
  dwells: DeviceDwell[];
}

class COPDwellsService {
  private baseUrl = '/api';

  /**
   * Fetch device dwells with filters
   */
  async getDwells(
    courseId?: string,
    deviceType?: string,
    entityType?: string,
    days: number = 7,
    limit: number = 100,
    entityId?: string,
    dateRange?: { startUtc: string; endUtc: string }
  ): Promise<DwellsResponse> {
    const queryParams = new URLSearchParams();
    
    if (courseId && courseId !== 'All Courses') {
      queryParams.append('course_id', courseId);
    }
    
    if (deviceType && deviceType !== 'All' && deviceType !== 'all') {
      queryParams.append('device_type', deviceType);
    }
    
    if (entityType && entityType !== 'All') {
      queryParams.append('entity_type', entityType);
    }
    
    if (entityId) {
      queryParams.append('entity_id', entityId);
    }
    
    if (dateRange) {
      queryParams.append('start_date', dateRange.startUtc);
      queryParams.append('end_date', dateRange.endUtc);
    } else {
      queryParams.append('days', days.toString());
    }
    queryParams.append('limit', limit.toString());

    const url = `${this.baseUrl}/cop-dwells${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[COP Dwells Service] Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[COP Dwells Service] Error fetching dwells:', error);
      throw error;
    }
  }

  /**
   * Format duration from seconds to human-readable string
   */
  formatDuration(seconds: number): string {
    if (seconds < 60) {
      return `${Math.floor(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  }
}

export const copDwellsService = new COPDwellsService();

